import React from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { editorStore } from "../../store/editorStore";
import { LengthUnit, lengthUnitLabel } from "../../utils/conversions";

type DisplayLengthUnitToggleProps = {
  className?: string;
  /**
   * `compact` — single square button (left panel / property panel header).
   * `segmented` — bordered mm / in pair when horizontal space allows.
   */
  variant?: "compact" | "segmented";
};

/**
 * Chip style for the property-panel header: matches the 34px icon buttons
 * (bordered, rounded-lg) with yellow active / grey inactive — not the toolbar track.
 */
const headerChipClass = (active: boolean) =>
  [
    "inline-flex h-full w-5 items-center justify-center rounded-md text-[10px] font-semibold lowercase leading-none transition-colors duration-150",
    active
      ? "bg-boniYellowPrimary text-blackplum"
      : "bg-transparent text-gray-500 hover:bg-gray-100 hover:text-gray-700",
  ].join(" ");

/**
 * Toggle for display length unit (mm | in). Geometry stays in world metres.
 */
export const DisplayLengthUnitToggle: React.FC<DisplayLengthUnitToggleProps> =
  observer(({ className = "", variant = "compact" }) => {
    const { t } = useTranslation();
    const unit = editorStore.displayLengthUnit;
    const label = lengthUnitLabel(unit);

    const setUnit = (next: LengthUnit) => {
      if (next !== unit) {
        editorStore.setDisplayLengthUnit(next);
      }
    };

    if (variant === "segmented") {
      return (
        <div
          className={`inline-flex h-[34px] items-stretch gap-0.5 rounded-lg border border-stone-500 bg-white p-0.5 ${className}`.trim()}
          role="group"
          aria-label={t("displayUnit.label", {
            defaultValue: "Length unit",
          })}
        >
          <button
            type="button"
            className={headerChipClass(unit === "mm")}
            onClick={() => setUnit("mm")}
            aria-pressed={unit === "mm"}
            title={t("displayUnit.millimeters", { defaultValue: "Millimeters" })}
          >
            mm
          </button>
          <span
            className="my-1 w-px shrink-0 bg-stone-300"
            aria-hidden
          />
          <button
            type="button"
            className={headerChipClass(unit === "in")}
            onClick={() => setUnit("in")}
            aria-pressed={unit === "in"}
            title={t("displayUnit.inches", { defaultValue: "Inches" })}
          >
            in
          </button>
        </div>
      );
    }

    return (
      <button
        type="button"
        aria-label={t("displayUnit.toggle", {
          defaultValue: "Toggle length unit",
        })}
        title={t("displayUnit.toggle", {
          defaultValue: "Toggle length unit",
        })}
        onClick={() => editorStore.toggleDisplayLengthUnit()}
        className={`relative flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-xs font-medium lowercase text-gray-700 transition-colors hover:bg-gray-50 ${className}`.trim()}
      >
        {label}
      </button>
    );
  });

export default DisplayLengthUnitToggle;
